import { call, put, takeLatest } from 'redux-saga/effects';
import i18n from '../../../i18n';
import vehicleTypeService from '../../../services/vehicleTypeService';
import {
  fetchVehicleTypes,
  fetchVehicleTypesSuccess,
  fetchVehicleTypesFailure,
  fetchVehicleTypeById,
  fetchVehicleTypeByIdSuccess,
  fetchVehicleTypeByIdFailure,
  addVehicleType,
  addVehicleTypeSuccess,
  addVehicleTypeFailure,
  updateVehicleType,
  updateVehicleTypeSuccess,
  updateVehicleTypeFailure,
  deleteVehicleType,
  deleteVehicleTypeSuccess,
  deleteVehicleTypeFailure,
  bulkDeleteVehicleTypes,
  bulkDeleteVehicleTypesSuccess,
  bulkDeleteVehicleTypesFailure,
} from './slice';

/**
 * Translate error message from backend
 */
function translateError(errorMessage) {
  if (!errorMessage) return i18n.t('vehicleTypes.errors.unknown');

  const errorMap = {
    'Vehicle type name already exists': 'vehicleTypes.errors.nameExists',
    'Vehicle type code already exists': 'vehicleTypes.errors.codeExists',
    'Vehicle type not found': 'vehicleTypes.errors.vehicleTypeNotFound',
    'Failed to add vehicle type': 'vehicleTypes.errors.addFailed',
    'Failed to update vehicle type': 'vehicleTypes.errors.updateFailed',
    'Failed to delete vehicle type': 'vehicleTypes.errors.deleteFailed',
    'Failed to fetch vehicle types': 'vehicleTypes.errors.fetchFailed',
    'Cannot delete vehicle type that is being used by vehicles': 'vehicleTypes.errors.cannotDeleteInUse',
  };

  for (const [key, translationKey] of Object.entries(errorMap)) {
    if (errorMessage.includes(key)) {
      return i18n.t(translationKey);
    }
  }

  return errorMessage;
}

function* handleFetchVehicleTypes(action) {
  try {
    const response = yield call(vehicleTypeService.getVehicleTypes, action.payload);
    yield put(fetchVehicleTypesSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchVehicleTypesFailure(translatedError));
  }
}

function* handleFetchVehicleTypeById(action) {
  try {
    const response = yield call(vehicleTypeService.getVehicleTypeById, action.payload);
    yield put(fetchVehicleTypeByIdSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchVehicleTypeByIdFailure(translatedError));
  }
}

function* handleAddVehicleType(action) {
  try {
    const response = yield call(vehicleTypeService.createVehicleType, action.payload);
    yield put(addVehicleTypeSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(addVehicleTypeFailure(translatedError));
  }
}

function* handleUpdateVehicleType(action) {
  try {
    const response = yield call(vehicleTypeService.updateVehicleType, action.payload);
    yield put(updateVehicleTypeSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(updateVehicleTypeFailure(translatedError));
  }
}

function* handleDeleteVehicleType(action) {
  try {
    const vehicleTypeId = action.payload;
    yield call(vehicleTypeService.deleteVehicleType, vehicleTypeId);
    yield put(deleteVehicleTypeSuccess(vehicleTypeId));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(deleteVehicleTypeFailure(translatedError));
  }
}

function* handleBulkDeleteVehicleTypes(action) {
  try {
    const vehicleTypeIds = action.payload;
    yield call(vehicleTypeService.bulkDeleteVehicleTypes, vehicleTypeIds);
    yield put(bulkDeleteVehicleTypesSuccess(vehicleTypeIds));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(bulkDeleteVehicleTypesFailure(translatedError));
  }
}

export function* watchVehicleTypesActions() {
  yield takeLatest(fetchVehicleTypes.type, handleFetchVehicleTypes);
  yield takeLatest(fetchVehicleTypeById.type, handleFetchVehicleTypeById);
  yield takeLatest(addVehicleType.type, handleAddVehicleType);
  yield takeLatest(updateVehicleType.type, handleUpdateVehicleType);
  yield takeLatest(deleteVehicleType.type, handleDeleteVehicleType);
  yield takeLatest(bulkDeleteVehicleTypes.type, handleBulkDeleteVehicleTypes);
}
